import {
  getBeeswaxThirdPartySegmentSearch,
  getBeeswaxThirdPartySegmentTree,
} from "@/apis/v1/beeswax";
import CustomAsyncTree from "@/components/Form/CustomAsyncTree";
import EICustomListItem from "@/components/List/EICustomListItem";
import { VERB_USAGE_SELECT_LIST } from "@/modules/Presets/TargetingExpression/data";
import { VERB_USAGE } from "@/modules/Presets/TargetingExpression/targetingExpression.constant";
import {
  MutatedDataProvider,
  SubModule,
  SubModuleCommonProps,
  VerbUsage,
  VerbUsageStateForSelectedItem,
} from "@/modules/Presets/TargetingExpression/targetingExpression.interface";

import ControlledInput from "@/components/Form/InputFields/ControlledInput";
import CustomCheckboxList from "@/components/List/CustomCheckBoxList";
import useDebounceSearch from "@/hooks/useDebounceSearch";
import { TName } from "@/types";
import { Box, CircularProgress, Typography, Button } from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import { QueryClient } from "@tanstack/react-query";
import { Dispatch, useEffect, useState } from "react";
import { FieldValues, useController } from "react-hook-form";
import { segmentKeys, useSegmentForm } from "./segment.hook";
import NoSegmentMatch from "./NoSegmentMatch";
const moduleName = "user";
const filterName = "segment";

export default function Segment<
  T extends FieldValues,
  U extends SubModule = "SEGMENT"
>({
  control,
  errors,
  parentName,
  nodes,
  setNodes,
  treeData,
  setTreeData,
  allNodes,
  setAllNodes,
  isLoading,
  isDataProviderFetching,
  initialData,
  valuesForSearchList,
  setValuesForSearchList,
  isEdit,
}: SegmentProps<T, U>) {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: Infinity,
      },
    },
  });

  const { search, setSearch, debounceValue } = useDebounceSearch(1000);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [searchListRender, setSearchListRender] = useState(
    debounceValue ? true : false
  );
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (debounceValue) {
      setSearchLoading(true);
      setSearchListRender(true);
      for (const listItem of valuesForSearchList) {
        let promise = null;
        const { dataProviderId } = listItem;
        const existingData = queryClient.getQueryData(
          segmentKeys.search({
            data_provider_id: dataProviderId,
            search: debounceValue,
          })
        );
        if (existingData) {
          promise = new Promise(() => existingData);
        } else {
          promise = queryClient.fetchQuery(
            segmentKeys.search({
              data_provider_id: dataProviderId,
              search: debounceValue,
            }),
            getBeeswaxThirdPartySegmentSearch,
            {
              meta: {
                queryParams: {
                  page: 1,
                  page_size: 20,
                  search: debounceValue,
                  data_provider_id: dataProviderId,
                },
              },
            }
          );
        }
        if (promise !== null) {
          listItem.isLoading = true;
          promise
            .then((response) => {
              listItem.children = response?.results?.map((item: any) => {
                const mappedItem = {
                  name: item.name,
                  id: item.key,
                  children: [],
                  isChildren: true,
                  isBranch: true,
                  parent: item.parent_key,
                  dataProviderId: item.data_provider_id,
                  verb_usage: item.verb_usage,
                };
                if (!allNodes[item.key])
                  setAllNodes((prev) => ({ ...prev, [item.key]: mappedItem }));
                return mappedItem;
              });
              listItem.isLoading = false;

              setValuesForSearchList((prev) => {
                const filteredList = prev.filter(
                  (item) => item.dataProviderId !== listItem.dataProviderId
                );
                return [...filteredList, listItem];
              });
            })
            .catch((error) => {
              console.log(error);
              listItem.isLoading = false;
              setValuesForSearchList((prev) => {
                const filteredList = prev.filter(
                  (item) => item.dataProviderId !== listItem.dataProviderId
                );
                return [...filteredList, listItem];
              });
            });
          setValuesForSearchList((prev) => {
            const filteredList = prev.filter(
              (item) => item.dataProviderId !== listItem.dataProviderId
            );

            return [...filteredList, listItem];
          });
        }
      }
    }
  }, [debounceValue]);

  useEffect(() => {
    function runEffect() {
      if (valuesForSearchList?.length) {
        const resolved = checkResolved(valuesForSearchList);

        if (resolved) setSearchLoading(false);
      } else {
        if (searchLoading) {
          setSearchLoading(false);
          setSearchListRender(false);
        }
      }
      if (!debounceValue) {
        if (searchListRender) setSearchListRender(false);
        if (searchLoading) setSearchLoading(false);
      }
    }
    runEffect();
  }, [JSON.stringify(valuesForSearchList), debounceValue]);
  function checkResolved(values: Record<string, any>[]) {
    return values.every((value: Record<string, any>) => {
      return !value.isLoading;
    });
  }
  const { control: localControl } = useSegmentForm({
    data: initialData,
  });
  const {
    field: { value: selected, onChange: setSelected },
  } = useController({
    control: localControl,
    name: "selected",
  });
  // states only cater to type/verbUsage of segment eg: grapeshot-76163: INCLUDE || grapeshot-76163:EXCLUDE  where grapeshot-76163 is segment
  const filterValueName = `${parentName}.${moduleName}.${filterName}.value`;
  const {
    field: { value: included, onChange: setIncluded },
    fieldState: includedFieldState,
  } = useController({
    control,
    name: `${filterValueName}.included` as TName<T>,
  });
  const {
    field: { value: excluded, onChange: setExcluded },
    fieldState: excludedFieldState,
  } = useController({
    control,
    name: `${filterValueName}.excluded` as TName<T>,
  });
  const {
    field: { value: required, onChange: setRequired },
    fieldState: requiredFieldState,
  } = useController({
    control,
    name: `${filterValueName}.required` as TName<T>,
  });

  // Hidden field to track segment changes and trigger dirty state
  const {
    field: { onChange: setSegmentChangeTracker },
  } = useController({
    control,
    name: `${filterValueName}._changeTracker` as TName<T>,
    defaultValue: 0 as any,
  });

  // Track changes with a simple counter
  const [changeCounter, setChangeCounter] = useState(0);

  // Wrapper functions to ensure form dirty state is triggered
  const updateIncluded = (newMap: Map<string, VerbUsage>) => {
    setIncluded(newMap);
    // Increment change tracker to trigger dirty state
    const newCount = changeCounter + 1;
    setChangeCounter(newCount);
    setSegmentChangeTracker(newCount);
  };

  const updateExcluded = (newMap: Map<string, VerbUsage>) => {
    setExcluded(newMap);
    // Increment change tracker to trigger dirty state
    const newCount = changeCounter + 1;
    setChangeCounter(newCount);
    setSegmentChangeTracker(newCount);
  };

  const updateRequired = (newMap: Map<string, VerbUsage>) => {
    setRequired(newMap);
    // Increment change tracker to trigger dirty state
    const newCount = changeCounter + 1;
    setChangeCounter(newCount);
    setSegmentChangeTracker(newCount);
  };

  const setTypeByVerbUsage = (verbUsage: VerbUsage, id: string) => {
    if (verbUsage === "ALL" || verbUsage === VERB_USAGE.INCLUDE)
      updateIncluded(setMapValueForUseState(included, id, VERB_USAGE.INCLUDE));
    if (verbUsage === VERB_USAGE.EXCLUDE)
      updateExcluded(setMapValueForUseState(excluded, id, VERB_USAGE.EXCLUDE));
    if (verbUsage === VERB_USAGE.REQUIRE)
      updateRequired(setMapValueForUseState(required, id, VERB_USAGE.REQUIRE));
  };
  const removeTypeByVerbUsage = (verbUsage: VerbUsage, id: string) => {
    if (verbUsage === "ALL" || verbUsage === VERB_USAGE.INCLUDE)
      updateIncluded(deleteFromMapForUseState(included, id));
    if (verbUsage === VERB_USAGE.EXCLUDE)
      updateExcluded(deleteFromMapForUseState(excluded, id));
    if (verbUsage === VERB_USAGE.REQUIRE)
      updateRequired(deleteFromMapForUseState(required, id));
  };
  const removeType = (id: string) => {
    if (included.has(id)) updateIncluded(deleteFromMapForUseState(included, id));
    if (excluded.has(id)) updateExcluded(deleteFromMapForUseState(excluded, id));
    if (required.has(id)) updateRequired(deleteFromMapForUseState(required, id));
  };
  const removeMultipleTypeByVerbUsage = (nodeIds: string[]) => {
    const removedFromIncluded: string[] = [];
    const removeFromExcluded: string[] = [];
    const removeFromRequried: string[] = [];
    nodeIds.forEach((id) => {
      if (included.has(id)) removedFromIncluded.push(id);
      if (excluded.has(id)) removeFromExcluded.push(id);
      if (required.has(id)) removeFromRequried.push(id);
    });
    if (removedFromIncluded.length)
      updateIncluded(removeMultipleValuesForMap(included, removedFromIncluded));
    if (removeFromExcluded.length)
      updateExcluded(removeMultipleValuesForMap(excluded, removeFromExcluded));
    if (removeFromRequried.length)
      updateRequired(removeMultipleValuesForMap(required, removeFromRequried));
  };
  const setMultipleTypeByVerbUsage = (nodeIds: string[]) => {
    const addToIncluded: string[] = [];
    const addToExcluded: string[] = [];
    const addToRequried: string[] = [];
    nodeIds.forEach((id) => {
      const nodeVerbUsage = allNodes[id].verb_usage;
      if (
        nodeVerbUsage === VERB_USAGE.ALL ||
        nodeVerbUsage === VERB_USAGE.INCLUDE
      )
        addToIncluded.push(id);
      if (nodeVerbUsage === VERB_USAGE.EXCLUDE) addToExcluded.push(id);
      if (nodeVerbUsage === VERB_USAGE.REQUIRE) addToRequried.push(id);
    });
    if (addToIncluded.length)
      updateIncluded(
        setMultipleValuesForMap(included, addToIncluded, VERB_USAGE.INCLUDE)
      );
    if (addToExcluded.length)
      updateExcluded(
        setMultipleValuesForMap(excluded, addToExcluded, VERB_USAGE.EXCLUDE)
      );
    if (addToRequried.length)
      updateRequired(
        setMultipleValuesForMap(required, addToRequried, VERB_USAGE.REQUIRE)
      );
  };
  const valueByVerbusage = (verbUsage: VerbUsage) => {
    if (verbUsage === VERB_USAGE.REQUIRE) return required;
    if (verbUsage === VERB_USAGE.INCLUDE || verbUsage === VERB_USAGE.ALL)
      return included;
    if (verbUsage === VERB_USAGE.EXCLUDE) return excluded;
  };
  const handleChangeSelectType = (
    lastVerbUsageValue: VerbUsage,
    currentVerbUsageValue: VerbUsage,
    id: string
  ) => {
    removeTypeByVerbUsage(lastVerbUsageValue, id);
    setTypeByVerbUsage(currentVerbUsageValue, id);
  };
  const handleDeleteListItem = (id: string, verbUsage: VerbUsage) => {
    setSelected((current: any) => current.filter((item: any) => item !== id));
    removeTypeByVerbUsage(verbUsage, id);
  };
  const handleLoadSegmentData = async ({
    key,
    dataProviderId,
    accountId,
  }: {
    key: string;
    dataProviderId: number;
    accountId: number;
  }) => {
    try {
      const data =
        queryClient.getQueryData(segmentKeys.tree({ key })) ??
        (await queryClient.fetchQuery(
          segmentKeys.tree({ key }),
          getBeeswaxThirdPartySegmentTree,
          {
            meta: {
              queryParams: {
                ...(accountId
                  ? {
                    data_provider_id: accountId,
                    parent_key__isempty: true,
                  }
                  : { parent_key: key }),
              },
            },
          }
        ));
      return data;
    } catch (error) {
      console.log(error);
    }
  };

  const handleToggleCheckBox = (value: Record<string, any>) => {
    //remove
    const selectedItem = selected.find((item: any) => item === value?.id);

    if (selectedItem) {
      const newSelectedValues = selected.filter(
        (item: any) => item !== selectedItem
      );

      setSelected(newSelectedValues);
      removeType(value?.id);
      return;
    }
    //add
    const newSelectedValues = Array.from(new Set([...selected, value.id]));

    setSelected(newSelectedValues);
    setTypeByVerbUsage(value?.verb_usage, value.id);
  };

  const customSetSelected = (nodes: string[]) => {
    setSelected(nodes);
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: isExpanded ? "auto" : "100%",
        gap: 2,
        ...(isExpanded && {
          minHeight: "100%",
          overflowY: "auto",
        }),
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <ControlledInput
          value={search}
          setValue={setSearch}
          type="primary"
          placeholder="Search Segment by Name, ID, or Alternative ID"
        />
      </Box>
      <Box
        sx={{
          flex: isExpanded ? "0 0 auto" : "1 1 0",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: isExpanded ? "0 0 auto" : "1 1 60%",
            minHeight: isExpanded ? "300px" : 0,
            height: isExpanded ? "300px" : "auto",
            backgroundColor: "background.paper",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            // Add subtle pulse animation when loading
            ...(isLoading && !debounceValue && treeData.length > 0 && {
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "2px",
                background: (theme) => `linear-gradient(90deg, 
                    transparent 0%, 
                    ${theme.palette.primary.main} 50%, 
                    transparent 100%)`,
                animation: "shimmer 2s infinite",
                zIndex: 2,
              },
              "@keyframes shimmer": {
                "0%": { transform: "translateX(-100%)" },
                "100%": { transform: "translateX(100%)" },
              },
            }),
          }}
        >
          {!debounceValue && treeData.length > 0 && (
            <Box sx={{ flex: 1, overflow: "auto", p: 2, position: "relative" }}>
              {/* Subtle loading indicator for data providers */}
              {isLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: "background.paper",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    boxShadow: 1,
                    zIndex: 1,
                  }}
                >
                  <CircularProgress size={16} sx={{ color: "primary.main" }} />
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Loading segments...
                  </Typography>
                </Box>
              )}
              <CustomAsyncTree<MutatedDataProvider, VerbUsage>
                handleLoadData={handleLoadSegmentData}
                selected={selected}
                setSelected={customSetSelected}
                nodes={nodes}
                setNodes={setNodes}
                setType={setTypeByVerbUsage}
                removeType={removeType}
                setMultipleType={setMultipleTypeByVerbUsage}
                removeMultipleType={removeMultipleTypeByVerbUsage}
                data={treeData}
                setData={setTreeData}
                setAllNodes={setAllNodes}
              />
            </Box>
          )}
          {!debounceValue && treeData.length === 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                flexDirection: "column",
                gap: 2,
              }}
            >
              {isLoading || isDataProviderFetching ? (
                <>
                  <CircularProgress size={40} />
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Loading data providers...
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" sx={{ color: "text.disabled" }}>
                  No data providers available
                </Typography>
              )}
            </Box>
          )}
          {debounceValue && searchListRender && (
            <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
              {!searchLoading && valuesForSearchList?.every(provider => 
                !provider.children || provider.children.length === 0
              ) ? (
                <NoSegmentMatch searchTerm={debounceValue} />
              ) : (
                <CustomCheckboxList
                  data={valuesForSearchList}
                  selected={selected}
                  handleToggleCheckBox={handleToggleCheckBox}
                  isLoading={searchLoading}
                />
              )}
            </Box>
          )}
        </Box>
        <Box
          sx={{
            flex: isExpanded ? "1 1 auto" : "0 0 auto",
            maxHeight: isExpanded ? "none" : "40%",
            minHeight: selected.length > 0 ? "150px" : "60px",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "background.paper",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            p: 2,
            position: "relative",
            transition: "all 0.3s ease-in-out",
          }}
        >
          {/* Loading overlay for selected segments */}
          {isLoading && selected.length > 0 && selected.some(id => !allNodes[id]) && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 1,
                zIndex: 10,
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <CircularProgress size={32} />
                <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
                  Loading segment details...
                </Typography>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Selected Segments
              </Typography>
              {selected.length > 0 && (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  ({selected.length})
                </Typography>
              )}
            </Box>
            <Button
              size="small"
              onClick={() => setIsExpanded(!isExpanded)}
              startIcon={isExpanded ? <ViewAgendaIcon /> : <ViewListIcon />}
              sx={{
                ml: "auto",
                minWidth: "auto",
                px: 1.5,
                py: 0.5,
                fontSize: "0.75rem",
                textTransform: "none",
              }}
            >
              {isExpanded ? "Compact" : "Show All"}
            </Button>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 0.8,
              overflowY: isExpanded ? "visible" : "auto",
              overflowX: "hidden",
              minHeight: 0,
              maxHeight: isExpanded ? "none" : "100%",
              pr: 1,
              // Custom scrollbar styling
              ...(!isExpanded && {
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "action.hover",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "action.selected",
                  borderRadius: "3px",
                  "&:hover": {
                    backgroundColor: "action.disabled",
                  },
                },
              }),
            }}
          >
            {selected.map((item: string, index: number) => {
              const dataItem = allNodes[item];
              const typeValue =
                included.get(item) ?? excluded.get(item) ?? required.get(item);
              // Check if this specific item is being loaded
              const isItemLoading = !dataItem && isLoading;
              return (
                <EICustomListItem<VerbUsage>
                  key={index}
                  data={dataItem || { name: "Loading..." }}
                  id={item}
                  selectList={filterSelectListByVerbUsage(
                    VERB_USAGE_SELECT_LIST,
                    dataItem?.verb_usage
                  )}
                  selectItemValue={typeValue}
                  onChangeSelectType={handleChangeSelectType}
                  onDeleteListItem={handleDeleteListItem}
                  isLoading={isItemLoading}
                />
              );
            })}
          </Box>
          {selected.length === 0 && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isLoading ? (
                <Box sx={{ textAlign: "center" }}>
                  <CircularProgress size={24} sx={{ color: "text.secondary" }} />
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 1,
                      color: "text.secondary",
                    }}
                  >
                    Loading segments...
                  </Typography>
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.disabled",
                    textAlign: "center",
                  }}
                >
                  No segments selected
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
type SegmentProps<T extends FieldValues, U> = U extends "SEGMENT"
  ? Segment<T>
  : never;
interface Segment<T extends FieldValues> extends SubModuleCommonProps<T> {
  nodes: Record<string, any>;
  setNodes: Dispatch<React.SetStateAction<Record<string, any>>>;
  allNodes: Record<string, any>;
  setAllNodes: Dispatch<React.SetStateAction<Record<string, any>>>;
  treeData: MutatedDataProvider[];
  setTreeData: Dispatch<React.SetStateAction<MutatedDataProvider[]>>;
  valuesForSearchList: Record<string, any>[];
  setValuesForSearchList: Dispatch<React.SetStateAction<Record<string, any>[]>>;
  isLoading: boolean;
  isDataProviderFetching: boolean;
  initialData: string[];
}

function filterSelectListByVerbUsage(
  list: typeof VERB_USAGE_SELECT_LIST,
  verbUsage: VerbUsage
) {
  if (verbUsage === VERB_USAGE.REQUIRE)
    return list.filter((item) => item.value === VERB_USAGE.REQUIRE);
  if (verbUsage === VERB_USAGE.INCLUDE)
    return list.filter((item) => item.value === VERB_USAGE.INCLUDE);
  if (verbUsage === VERB_USAGE.EXCLUDE)
    return list.filter((item) => item.value === VERB_USAGE.EXCLUDE);

  return list;
}

function deleteFromMapForUseState(
  state: VerbUsageStateForSelectedItem,
  id: string
) {
  state.delete(id);
  return new Map(state);
}
function setMapValueForUseState(
  state: Map<string, any>,
  id: string,
  value: VerbUsage
) {
  state.set(id, value);
  return new Map(state);
}

function setMultipleValuesForMap(
  state: Map<string, any>,
  newState: string[],
  type: VerbUsage
) {
  newState.forEach((id) => {
    state.set(id, type);
  });

  return new Map(state);
}
function removeMultipleValuesForMap(
  state: Map<string, any>,
  valuesToRemove: string[]
) {
  valuesToRemove.forEach((value) => {
    state.delete(value);
  });

  return new Map(state);
}
